import { useQuery } from "@tanstack/react-query";
import { UserSchema, type User } from "@trivoa/shared";
import { z } from "zod";
import { api } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
const CurrentUserResponseSchema = z.object({ user: UserSchema });
/** Prefix for invalidation only; accepted identity is always generation-bound. */
export const currentUserQueryKey = ["auth", "current-user"] as const;
export const currentUserKey = (sessionKey: string) =>
  [...currentUserQueryKey, sessionKey] as const;
export async function fetchCurrentUser(options?: {
  signal?: AbortSignal;
}): Promise<User> {
  const response = options
    ? await api.get("/me", {
        errorPresentation: "caller",
        signal: options.signal,
      })
    : await api.get("/me");
  return CurrentUserResponseSchema.parse(response.data).user;
}
export function useCurrentUser(enabled = true) {
  const auth = useAuth(),
    session = auth.session;
  const query = useQuery({
    queryKey: currentUserKey(session.key),
    queryFn: async ({ signal }) => {
      if (!session.isCurrent())
        throw new DOMException("Authentication context changed", "AbortError");
      const user = await fetchCurrentUser({
        signal: AbortSignal.any([
          signal,
          session.signal,
          AbortSignal.timeout(20_000),
        ]),
      });
      if (!session.isCurrent())
        throw new DOMException("Authentication context changed", "AbortError");
      if (user.clerkId !== auth.userId)
        throw new Error("Authenticated identity could not be verified");
      return user;
    },
    enabled: enabled && auth.isLoaded && auth.isSignedIn && session.isCurrent(),
    staleTime: 60_000,
    gcTime: 0,
    retry: false,
  });
  return {
    ...query,
    data:
      enabled &&
      auth.isSignedIn &&
      session.isCurrent() &&
      !query.isError &&
      query.data?.clerkId === auth.userId
        ? query.data
        : undefined,
  };
}
